import React from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
  Switch,
} from "@mui/material";
import { getEventLabel } from "../../../constants/automationEvents";

const SettingsStep = ({ formData, onSettingsChange }) => {
  return (
    <Box sx={{ pt: 2.5 }}>
      {/* Rule Settings Row */}
      <Box display="flex" alignItems="flex-start" gap={3}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: "0.92rem",
            whiteSpace: "nowrap",
            mt: 2,
            minWidth: 110,
          }}
        >
          Rule Settings
        </Typography>

        <FormControl sx={{ flex: 1 }}>
          <InputLabel sx={{ fontSize: "0.88rem" }}>Status</InputLabel>
          <Select
            value={formData.settings.enabled}
            label="Status"
            onChange={(e) => onSettingsChange("enabled", e.target.value)}
            sx={{
              borderRadius: 2,
              fontSize: "0.92rem",
              "& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
              "& .MuiSelect-select": { py: 1.5, px: 2 },
            }}
          >
            <MenuItem value={true}>Enabled</MenuItem>
            <MenuItem value={false}>Disabled</MenuItem>
          </Select>
        </FormControl>

        <TextField
          type="number"
          label="Delay (minutes)"
          value={formData.settings.delayMinutes}
          onChange={(e) =>
            onSettingsChange("delayMinutes", parseInt(e.target.value) || 0)
          }
          helperText="Wait before sending"
          sx={{
            flex: 1.2,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "0.92rem",
            },
            "& .MuiOutlinedInput-input": { py: 1.5, px: 2 },
            "& .MuiInputLabel-root": { fontSize: "0.88rem" },
            "& .MuiFormHelperText-root": {
              fontSize: "0.72rem",
              mt: 0.5,
              color: "text.secondary",
            },
          }}
        />
      </Box>

      {/* Divider */}
      <Divider sx={{ my: 3 }} />

      {/* Business Hours Row */}
      <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
        <Typography
          sx={{
            fontWeight: 500,
            fontSize: "0.88rem",
            lineHeight: 1.4,
            minWidth: 130,
          }}
        >
          Send only during{"\n"}business hours
        </Typography>

        <Switch
          checked={formData.settings.sendBetweenHours.enabled}
          onChange={() =>
            onSettingsChange("sendBetweenHours", {
              ...formData.settings.sendBetweenHours,
              enabled: !formData.settings.sendBetweenHours.enabled,
            })
          }
          color="primary"
          size="small"
        />

        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "0.72rem",
            letterSpacing: 0.5,
            color: formData.settings.sendBetweenHours.enabled
              ? "primary.main"
              : "text.disabled",
          }}
        >
          {formData.settings.sendBetweenHours.enabled ? "ENABLED" : "DISABLED"}
        </Typography>

        {formData.settings.sendBetweenHours.enabled && (
          <>
            <TextField
              type="time"
              label="Start Time"
              value={formData.settings.sendBetweenHours.start}
              onChange={(e) =>
                onSettingsChange("sendBetweenHours", {
                  ...formData.settings.sendBetweenHours,
                  start: e.target.value,
                })
              }
              inputProps={{ step: 300 }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                width: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.88rem",
                },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
                "& .MuiInputLabel-root": { fontSize: "0.82rem" },
              }}
            />
            <TextField
              type="time"
              label="End Time"
              value={formData.settings.sendBetweenHours.end}
              onChange={(e) =>
                onSettingsChange("sendBetweenHours", {
                  ...formData.settings.sendBetweenHours,
                  end: e.target.value,
                })
              }
              inputProps={{ step: 300 }}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{
                width: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  fontSize: "0.88rem",
                },
                "& .MuiOutlinedInput-notchedOutline": { borderRadius: 2 },
                "& .MuiInputLabel-root": { fontSize: "0.82rem" },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.78rem", fontStyle: "italic" }}
            >
              Business Hours (Optional)
            </Typography>
          </>
        )}
      </Box>

      {/* Rule Summary */}
      <Box
        sx={{
          mt: 4,
          border: "1px solid",
          borderColor: "#e5e7eb",
          borderRadius: 4,
          bgcolor: "#f9fafb",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: "primary.main",
              letterSpacing: 1.5,
              fontSize: "0.74rem",
              textTransform: "uppercase",
            }}
          >
            Rule Summary
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
          {/* Row 1: Name + Trigger */}
          <Box display="flex" sx={{ mb: 3 }}>
            <Box sx={{ width: "50%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#6b7280",
                  fontSize: "0.7rem",
                  letterSpacing: 1,
                  mb: 0.5,
                }}
              >
                Name
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 500 }}>
                {formData.name || "\u2014"}
              </Typography>
            </Box>
            <Box sx={{ width: "50%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#6b7280",
                  fontSize: "0.7rem",
                  letterSpacing: 1,
                  mb: 0.5,
                }}
              >
                Trigger
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 500 }}>
                {getEventLabel(formData.trigger.event) || "\u2014"}
              </Typography>
            </Box>
          </Box>

          {/* Row 2: Template + Enabled */}
          <Box display="flex">
            <Box sx={{ width: "50%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#6b7280",
                  fontSize: "0.7rem",
                  letterSpacing: 1,
                  mb: 0.5,
                }}
              >
                Template
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 500 }}>
                {formData.action.channel.whatsapp?.templateName || "\u2014"}
              </Typography>
            </Box>
            <Box sx={{ width: "50%" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#6b7280",
                  fontSize: "0.7rem",
                  letterSpacing: 1,
                  mb: 0.5,
                }}
              >
                Enabled
              </Typography>
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  component="span"
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: formData.settings.enabled
                      ? "#10b981"
                      : "error.main",
                    display: "inline-block",
                  }}
                />
                <Typography sx={{ fontSize: "1.05rem", fontWeight: 500 }}>
                  {formData.settings.enabled ? "Yes" : "No"}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsStep;
